// Phase 6.0 STEP B · 숏클립 전체화면 세로 피드 (TikTok 스타일)
// Prod-S3 · 다중 상품 연결 · linked_products 배열 지원 + Bottom Sheet
// Prod-S5 · Viewer UX Fix
//   - Seller Profile 진입 시 seller_id 스코프 유지 (다른 판매자 clip 섞이지 않음)
//   - iframe embed 플랫폼(YouTube/TikTok/Instagram) 은 native volume 사용 · 중복 mute 아이콘 제거
//   - PC/Mobile 공통 prev/next 명시 버튼 (우측 하단, 상품 CTA 영역과 분리)
//   - 첫 clip 즉시 재생 · initialClip 있으면 background fetch 병렬
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, ShoppingCart, ExternalLink, Store, Play, Volume2, VolumeX, Package, ChevronUp, ChevronDown } from 'lucide-react';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const IMPRESSION_SEEN = new Set();

const trackEvent = (clipId, event, extra = {}) => {
  if (!clipId || !event) return;
  api.post(`/public/short-clips/${clipId}/event`, { event, ...extra }).catch(() => {});
};

const attrQuery = (clip, productId) => {
  if (!clip?.clip_id) return '';
  const p = new URLSearchParams();
  p.set('from', 'clip');
  p.set('clip_id', clip.clip_id);
  if (clip.platform) p.set('platform', clip.platform);
  if (productId) p.set('src_pid', productId);
  return p.toString();
};

// ---------------------------------------------------------------------------
// Bottom Sheet · 다중 상품 리스트 · 영상 재생 중단 없이 오버레이
// ---------------------------------------------------------------------------
function ProductSheet({ open, onClose, products, sellerName, clip }) {
  if (!open) return null;
  return (
    <div className="absolute inset-0 z-[80] flex items-end justify-center"
      onClick={onClose} data-testid="feed-product-sheet-backdrop">
      <div className="absolute inset-0 bg-black/60 animate-fadeIn"/>
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg max-h-[70vh] bg-white dark:bg-[#141414] rounded-t-3xl shadow-2xl overflow-hidden"
        style={{ animation: 'slideUp 220ms ease-out' }}
        data-testid="feed-product-sheet">
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-black/20 dark:bg-white/20"/>
        </div>
        <div className="flex items-center justify-between px-5 py-2 border-b border-black/5 dark:border-white/5">
          <div className="flex items-center gap-2">
            <Package size={16} className="text-[#FF3B30]"/>
            <p className="text-sm font-black" data-testid="feed-sheet-title">
              연결 상품 {products.length}개
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
            data-testid="feed-sheet-close">
            <X size={16}/>
          </button>
        </div>
        {sellerName && (
          <p className="px-5 pt-1.5 text-[11px] text-gray-500">
            <Store size={10} className="inline -mt-0.5 mr-1"/> {sellerName}
          </p>
        )}
        <ul className="overflow-y-auto max-h-[calc(70vh-6rem)] divide-y divide-black/5 dark:divide-white/5">
          {products.map((lp, i) => {
            const snap = lp.product_snapshot || {};
            const pid = lp.product_id;
            const q = attrQuery(clip, pid);
            return (
              <li key={pid || i} className="p-4 flex items-center gap-3"
                data-testid={`feed-sheet-item-${pid}`}>
                {snap.image ? (
                  <img src={snap.image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0 bg-gray-100"/>
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Package size={20} className="text-gray-400"/>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {i === 0 && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-[#FF3B30] text-white">대표</span>
                    )}
                    <p className="text-xs font-bold truncate">{snap.name || '(상품)'}</p>
                  </div>
                  <p className="text-base font-black text-[#FF3B30] mt-0.5"
                    style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {won(snap.sale_price)}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <Link to={`/product/${pid}?${q}`}
                    onClick={() => { trackEvent(clip.clip_id, 'product_click', { product_id: pid, platform_hint: clip.platform }); onClose(); }}
                    className="px-3 py-1.5 rounded-full bg-black/5 dark:bg-white/10 text-black dark:text-white font-black text-[11px] inline-flex items-center gap-1"
                    data-testid={`feed-sheet-view-${pid}`}>
                    <ExternalLink size={10}/> 상품
                  </Link>
                  <Link to={`/product/${pid}?buy=1&${q}`}
                    onClick={() => { trackEvent(clip.clip_id, 'buy_click', { product_id: pid, platform_hint: clip.platform }); onClose(); }}
                    className="px-3 py-1.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-black text-[11px] inline-flex items-center gap-1"
                    data-testid={`feed-sheet-buy-${pid}`}>
                    <ShoppingCart size={10}/> 구매
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ClipItem({ clip, muted, onToggleMute, initialActive }) {
  const videoRef = useRef(null);
  const [inView, setInView] = useState(!!initialActive);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Prod-S1.1 / T-S2 · IG-S2 · 플랫폼별 렌더러 분기.
  const isYoutube   = clip.platform === 'youtube'   && clip.embed_url;
  const isTiktok    = clip.platform === 'tiktok'    && clip.embed_url;
  const isInstagram = clip.platform === 'instagram' && clip.embed_url;
  const isIframe    = isYoutube || isTiktok || isInstagram;
  const isVideo     = !isIframe && clip.video_url && !/\.gif(\?|$)/i.test(clip.video_url);

  const products = useMemo(() => {
    const lp = Array.isArray(clip.linked_products) ? clip.linked_products : [];
    if (lp.length > 0) return lp;
    if (clip.product_snapshot || clip.product_id) {
      return [{
        product_id: clip.product_id,
        product_snapshot: clip.product_snapshot,
        sort_order: 0,
      }];
    }
    return [];
  }, [clip.linked_products, clip.product_snapshot, clip.product_id]);

  const isMulti = products.length > 1;
  const primary = products[0] || {};
  const primaryPid = primary.product_id || clip.product_id;
  const primaryProduct = primary.product_snapshot || clip.product_snapshot || {};

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      setInView(entry.intersectionRatio > 0.5);
    }, { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (isIframe) {
      if (inView && !IMPRESSION_SEEN.has(clip.clip_id)) {
        IMPRESSION_SEEN.add(clip.clip_id);
        trackEvent(clip.clip_id, 'impression', { platform_hint: clip.platform });
      }
      return;
    }
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v || typeof v.play !== 'function') return;
    if (inView) {
      v.play().catch(() => { /* autoplay blocked · user gesture needed */ });
      if (!IMPRESSION_SEEN.has(clip.clip_id)) {
        IMPRESSION_SEEN.add(clip.clip_id);
        trackEvent(clip.clip_id, 'impression', { platform_hint: clip.platform });
      }
    } else {
      v.pause();
    }
  }, [inView, isIframe, isVideo, clip.clip_id, clip.platform]);

  const seller = clip.seller_snapshot || {};

  // Instagram permalink → embed URL 변환 (embed.js 없는 정적 iframe · captioned=0)
  const igEmbedSrc = useMemo(() => {
    if (!isInstagram) return null;
    const permalink = (clip.embed_url || clip.source_url || '').replace(/\/$/, '');
    return `${permalink}/embed/captioned/?cr=1&v=14`;
  }, [isInstagram, clip.embed_url, clip.source_url]);

  return (
    <div className="relative w-full h-full flex items-center justify-center bg-black snap-start snap-always" data-testid={`feed-clip-${clip.clip_id}`}>
      {isYoutube ? (
        <iframe ref={videoRef}
          src={`${clip.embed_url}?autoplay=${inView ? 1 : 0}&mute=${muted ? 1 : 0}&playsinline=1&modestbranding=1&rel=0&loop=1&playlist=${clip.external_content_id}`}
          title={clip.title || 'YouTube Shorts'}
          className="w-full h-full"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          data-testid={`feed-yt-iframe-${clip.clip_id}`}
        />
      ) : isTiktok ? (
        <iframe ref={videoRef}
          src={`${clip.embed_url}?autoplay=${inView ? 1 : 0}&mute=${muted ? 1 : 0}&loop=1&music_info=0&description=0`}
          title={clip.title || 'TikTok Shorts'}
          className="w-full h-full"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          data-testid={`feed-tt-iframe-${clip.clip_id}`}
        />
      ) : isInstagram ? (
        <iframe ref={videoRef}
          src={igEmbedSrc}
          title={clip.title || 'Instagram Reels'}
          className="w-full h-full"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen scrolling="no"
          data-testid={`feed-ig-iframe-${clip.clip_id}`}
        />
      ) : isVideo ? (
        <video ref={videoRef} src={clip.video_url} poster={clip.thumbnail_url}
          loop muted={muted} playsInline preload="metadata"
          className="w-full h-full object-contain"/>
      ) : (
        <img ref={videoRef} src={clip.video_url || clip.thumbnail_url} alt="" className="w-full h-full object-contain"/>
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"/>

      {clip.is_sponsored && (
        <span className="absolute top-4 left-16 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-black" data-testid="feed-sponsored-badge">Sponsored</span>
      )}
      {clip.is_featured && !clip.is_sponsored && (
        <span className="absolute top-4 left-16 px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black">★ LIVEMOA 추천</span>
      )}

      {/* Prod-S5 · iframe 플랫폼은 자체 native volume control 을 가지므로
          LIVEMOA custom mute 버튼은 uploaded <video> 에서만 노출 (중복 제거). */}
      {isVideo && (
        <button onClick={onToggleMute} className="absolute top-4 right-4 z-[70] w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur active:scale-95 transition-transform" data-testid="feed-mute-toggle" aria-label={muted ? '음소거 해제' : '음소거'}>
          {muted ? <VolumeX size={16}/> : <Volume2 size={16}/>}
        </button>
      )}

      {/* Overlay content — seller/product/CTA */}
      <div className="absolute inset-x-0 bottom-0 p-5 pb-6 text-white pointer-events-none">
        <div className="max-w-lg mx-auto pointer-events-auto pr-14">
          <Link to={`/seller/${clip.seller_id}`} className="inline-flex items-center gap-2 mb-2" data-testid={`feed-seller-${clip.clip_id}`}>
            {seller.profile_image ? <img src={seller.profile_image} alt="" className="w-8 h-8 rounded-full border-2 border-white"/>
              : <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Store size={14}/></div>}
            <span className="text-sm font-black drop-shadow">{seller.name || '판매자'}</span>
          </Link>
          <p className="text-lg font-black leading-tight mb-1 drop-shadow-md">{primaryProduct.name || clip.title}</p>
          <p className="text-2xl font-black text-[#FF9F0A] drop-shadow-md" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(primaryProduct.sale_price)}</p>
          {clip.description && <p className="text-xs text-white/80 mt-1 line-clamp-2">{clip.description}</p>}

          {isMulti ? (
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setSheetOpen(true); trackEvent(clip.clip_id, 'list_open', { platform_hint: clip.platform }); }}
                className="px-4 py-2.5 rounded-full bg-white text-black font-black text-xs inline-flex items-center gap-1.5 shadow-lg"
                data-testid={`feed-view-multi-${clip.clip_id}`}>
                <Package size={13}/> {products.length}개 상품 보기
              </button>
              <Link to={`/product/${primaryPid}?buy=1&${attrQuery(clip, primaryPid)}`}
                onClick={() => trackEvent(clip.clip_id, 'buy_click', { product_id: primaryPid, platform_hint: clip.platform })}
                className="px-4 py-2.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-black text-xs inline-flex items-center gap-1"
                data-testid={`feed-buy-${clip.clip_id}`}>
                <ShoppingCart size={12}/> 대표상품 구매
              </Link>
            </div>
          ) : primaryPid ? (
            <div className="flex gap-2 mt-4">
              <Link to={`/product/${primaryPid}?${attrQuery(clip, primaryPid)}`}
                onClick={() => trackEvent(clip.clip_id, 'product_click', { product_id: primaryPid, platform_hint: clip.platform })}
                className="px-4 py-2.5 rounded-full bg-white/90 hover:bg-white text-black font-black text-xs inline-flex items-center gap-1"
                data-testid={`feed-view-${clip.clip_id}`}>
                <ExternalLink size={12}/> 상품보기
              </Link>
              <Link to={`/product/${primaryPid}?buy=1&${attrQuery(clip, primaryPid)}`}
                onClick={() => trackEvent(clip.clip_id, 'buy_click', { product_id: primaryPid, platform_hint: clip.platform })}
                className="px-4 py-2.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-black text-xs inline-flex items-center gap-1"
                data-testid={`feed-buy-${clip.clip_id}`}>
                <ShoppingCart size={12}/> 바로구매
              </Link>
            </div>
          ) : null}
        </div>
      </div>

      {isMulti && (
        <ProductSheet open={sheetOpen} onClose={() => setSheetOpen(false)}
          products={products} sellerName={seller.name} clip={clip}/>
      )}
    </div>
  );
}

export default function ShortClipFeed() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialClip = location?.state?.initialClip || null;
  const sellerScope = !!location?.state?.sellerScope;
  const sellerId    = location?.state?.sellerId || initialClip?.seller_id || null;

  const [clips, setClips] = useState(() => (initialClip ? [initialClip] : []));
  const [page, setPage] = useState(1);
  const [muted, setMuted] = useState(true);
  const [empty, setEmpty] = useState(false);
  const containerRef = useRef(null);

  const load = useCallback(async (p) => {
    try {
      let items = [];
      if (sellerScope && sellerId) {
        // Prod-S5 · Seller Profile 진입: 해당 판매자 공개 clip 만 (다른 판매자 섞이지 않음)
        // Backend `/public/sellers/{sid}/short-clips` 는 pagination 없음 · 최대 48개.
        if (p === 1) {
          const r = await api.get(`/public/sellers/${sellerId}/short-clips?limit=48`);
          items = r.data?.items || [];
        }
      } else {
        // Global feed 진입: 여러 판매자 round-robin (기존 유지)
        const r = await api.get(`/public/short-clips/feed?page=${p}&page_size=15`);
        items = r.data?.items || [];
      }
      setClips((prev) => {
        if (p === 1) {
          if (initialClip) {
            const rest = items.filter((c) => c.clip_id !== initialClip.clip_id);
            return [initialClip, ...rest];
          }
          return items;
        }
        const seen = new Set(prev.map((c) => c.clip_id));
        return [...prev, ...items.filter((c) => !seen.has(c.clip_id))];
      });
      setEmpty(p === 1 && items.length === 0 && !initialClip);
    } catch { /* silent */ }
  }, [initialClip, sellerScope, sellerId]);
  useEffect(() => { load(1); }, [load]);

  // Global feed 만 pagination · Seller scope 는 최초 fetch 48개로 충분
  useEffect(() => {
    if (sellerScope) return;
    if (!clips.length) return;
    const last = document.querySelector(`[data-testid="feed-clip-${clips[clips.length - 1].clip_id}"]`);
    if (!last) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setPage((p) => { const np = p + 1; load(np); return np; });
      }
    }, { threshold: 0.5 });
    io.observe(last);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips.length, sellerScope]);

  // Prod-S5 · PC/Mobile 공통 prev/next 명시 버튼 · snap scroll 로 이동
  const goToIndex = useCallback((idx) => {
    if (!containerRef.current) return;
    const target = containerRef.current.children?.[idx];
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    if (!containerRef.current || !clips.length) return;
    const items = Array.from(containerRef.current.children);
    if (!items.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio >= 0.6) {
          const i = items.indexOf(entry.target);
          if (i >= 0) setActiveIdx(i);
        }
      });
    }, { root: containerRef.current, threshold: [0, 0.6, 1] });
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [clips.length]);

  return (
    <div className="fixed inset-0 z-[60] bg-black" data-testid="short-clip-feed">
      <button onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-[75] w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur active:scale-95 transition-transform"
        data-testid="feed-close" aria-label="닫기">
        <X size={18}/>
      </button>
      {sellerScope && (
        <span className="absolute top-4 left-16 z-[74] px-2.5 py-1 rounded-full bg-white/15 text-white text-[10px] font-black backdrop-blur"
          data-testid="feed-scope-badge">
          이 판매자의 쇼츠 · {clips.length}
        </span>
      )}
      {empty ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-white text-center px-6" data-testid="feed-empty">
          <Play size={48} className="text-white/40 mb-3"/>
          <p className="text-lg font-black">아직 등록된 숏클립이 없습니다</p>
          <p className="text-sm text-white/60 mt-1">판매자가 쇼츠를 등록하면 여기에 노출됩니다.</p>
        </div>
      ) : (
        <>
          <div ref={containerRef} className="h-full overflow-y-scroll snap-y snap-mandatory
            [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {clips.map((c, idx) => (
              <div key={c.clip_id} className="w-full h-full snap-start snap-always" style={{ height: '100dvh' }}>
                <ClipItem clip={c} muted={muted} onToggleMute={() => setMuted((m) => !m)} initialActive={idx === 0}/>
              </div>
            ))}
          </div>

          {/* Prod-S5 · 우측 하단 세로 prev/next · CTA 영역과 분리 · 항상 클릭 가능한 hit-area */}
          <div className="absolute right-3 bottom-24 md:bottom-28 z-[75] flex flex-col gap-2" data-testid="feed-nav-controls">
            <button onClick={() => goToIndex(Math.max(0, activeIdx - 1))}
              disabled={activeIdx <= 0}
              className="w-11 h-11 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur active:scale-95 transition-transform disabled:opacity-30 disabled:pointer-events-none"
              data-testid="feed-prev" aria-label="이전 숏츠">
              <ChevronUp size={20}/>
            </button>
            <button onClick={() => goToIndex(Math.min(clips.length - 1, activeIdx + 1))}
              disabled={activeIdx >= clips.length - 1}
              className="w-11 h-11 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur active:scale-95 transition-transform disabled:opacity-30 disabled:pointer-events-none"
              data-testid="feed-next" aria-label="다음 숏츠">
              <ChevronDown size={20}/>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
